import React, { useState } from 'react'
import classNames from 'classnames'
import { createPortal } from 'react-dom'
import { CaretLeftIcon } from '@/atoms/Icons/CaretLeftIcon'
import { CheckIcon } from '@/atoms/Icons/CheckIcon'

type SettingsPage = 'main' | 'quality' | 'audio' | 'subtitles' | 'speed'

interface SettingsOption {
  id: string
  label: string
  currentValue: string
  page: SettingsPage
}

interface SettingsListItem {
  id: string
  label: string
  active: boolean
}

interface HlsSettingsPanelProps {
  open: boolean
  onClose: () => void
  options: SettingsOption[]
  pages: Record<string, { title: string; items: SettingsListItem[]; onSelect: (id: string) => void }>
  /** When set, the panel opens directly to this sub-page instead of the main menu. */
  initialPage?: SettingsPage
}

/**
 * Settings panel for the HLS.js player.
 * Shows a main menu with sub-pages for quality, audio, subtitles, and speed.
 * Renders as an absolutely-positioned panel above the control bar.
 */
export const HlsSettingsPanel: React.FC<HlsSettingsPanelProps> = ({ open, onClose, options, pages, initialPage }) => {
  const [currentPage, setCurrentPage] = useState<SettingsPage>('main')
  const prevOpenRef = React.useRef(false)
  const prevInitialPageRef = React.useRef(initialPage)

  // Set page synchronously during render (not in an effect) to avoid a flash
  // of the main menu when opening to a specific sub-page.
  // Also switch pages when initialPage changes while already open (e.g. clicking
  // the Audio button while the Subtitles sub-page is showing).
  if (open && !prevOpenRef.current) {
    setCurrentPage(initialPage ?? 'main')
  } else if (open && initialPage && initialPage !== prevInitialPageRef.current) {
    setCurrentPage(initialPage)
  }
  prevOpenRef.current = open
  prevInitialPageRef.current = initialPage

  const handleBack = () => {
    setCurrentPage('main')
  }

  const handleOptionClick = (page: SettingsPage) => {
    setCurrentPage(page)
  }

  const handleItemSelect = (pageId: string, itemId: string) => {
    pages[pageId]?.onSelect(itemId)
    setCurrentPage('main')
    onClose()
  }

  if (!open) return null

  // Portal into the player container so the panel is positioned relative to the
  // full player area, not inside the control bar (which would clip/cover it).
  const portalTarget = document.getElementById('hlsjs-player')
  if (!portalTarget) return null

  const subPage = currentPage !== 'main' ? pages[currentPage] : null

  return createPortal(
    <div
      data-hls-settings-panel
      className="absolute bottom-16 right-3 z-50 max-h-[70%] min-w-[220px] overflow-y-auto rounded-lg bg-core-gray-950/90 py-1.5 text-sm text-white shadow-lg backdrop-blur-sm"
      onClick={(e) => e.stopPropagation()}
    >
      {currentPage === 'main' ? (
        <MainMenu options={options} onOptionClick={handleOptionClick} />
      ) : subPage ? (
        <SubPageMenu
          title={subPage.title}
          items={subPage.items}
          onBack={handleBack}
          onSelect={(id) => handleItemSelect(currentPage, id)}
        />
      ) : null}
    </div>,
    portalTarget,
  )
}

// --- Main menu ---

interface MainMenuProps {
  options: SettingsOption[]
  onOptionClick: (page: SettingsPage) => void
}

const MainMenu: React.FC<MainMenuProps> = ({ options, onOptionClick }) => {
  return (
    <ul>
      {options.map((option) => (
        <li key={option.id}>
          <button
            type="button"
            className="flex w-full items-center justify-between gap-4 px-4 py-2 text-left transition-colors hover:bg-white/10"
            onClick={() => onOptionClick(option.page)}
          >
            <span className="font-medium">{option.label}</span>
            <span className="flex items-center gap-1 text-core-gray-400">
              <span className="truncate text-xs">{option.currentValue}</span>
              <CaretRightIcon />
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}

// --- Sub-page menu ---

interface SubPageMenuProps {
  title: string
  items: SettingsListItem[]
  onBack: () => void
  onSelect: (id: string) => void
}

const SubPageMenu: React.FC<SubPageMenuProps> = ({ title, items, onBack, onSelect }) => {
  return (
    <>
      <button
        type="button"
        className="flex w-full items-center gap-2 border-b border-white/10 px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-core-gray-300 transition-colors hover:bg-white/10"
        onClick={onBack}
      >
        <CaretLeftIcon size={14} color="white" />
        <span>{title}</span>
      </button>
      <ul className="py-1">
        {items.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              className={classNames(
                'flex w-full items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-white/10',
                item.active && 'text-white',
                !item.active && 'text-core-gray-400',
              )}
              onClick={() => onSelect(item.id)}
            >
              <span className="w-4 shrink-0">{item.active && <CheckIcon size={14} color="white" />}</span>
              <span>{item.label}</span>
            </button>
          </li>
        ))}
      </ul>
    </>
  )
}

const CaretRightIcon: React.FC = () => <CaretLeftIcon size={14} color="core-gray-400" className="rotate-180" />
